import React from 'react';

export default function GurgencilerQ12026() {
  const formatTL = (n, fd = 0) => `₺${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: fd, maximumFractionDigits: fd })}`;
  const formatInt = (n) => Number(Math.round(n || 0)).toLocaleString('tr-TR');
  const clampNonNeg = (n) => Math.max(0, Number(n || 0));

  // Compact formatters (e.g., 101111 -> 101.1K, 11000000 -> 11.0M)
  const stripTrailingZero = (s) => s.replace(/\.0$/, '');
  function formatNumberCompact(value) {
    const n = Number(value || 0);
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${stripTrailingZero((n / 1e9).toFixed(1))}B`;
    if (abs >= 1e6) return `${stripTrailingZero((n / 1e6).toFixed(1))}M`;
    if (abs >= 1e3) return `${stripTrailingZero((n / 1e3).toFixed(1))}K`;
    return `${Math.round(n)}`;
  }
  function formatTLCompact(value) {
    const n = Number(value || 0);
    const abs = Math.abs(n);
    if (abs >= 1e9) return `₺${stripTrailingZero((n / 1e9).toFixed(1))}B`;
    if (abs >= 1e6) return `₺${stripTrailingZero((n / 1e6).toFixed(1))}M`;
    if (abs >= 1e3) return `₺${stripTrailingZero((n / 1e3).toFixed(1))}K`;
    return `₺${Math.round(n).toLocaleString('tr-TR')}`;
  }

  // Channel icons (public/)
  const channelIcons = {
    'Google Ads': '/google_ads-512.webp',
    'Meta': '/6033716.png',
    'Yandex Ads': '/Yandex_Ads_logo.png',
  };
  const channelIconSize = {
    'Yandex Ads': 'w-6 h-6', // slightly larger for Yandex as requested
  };

  // Performance assumptions helpers
  // Supported models: CPC, CPM
  function computePerformance(row) {
    const budget = clampNonNeg(row.budget);
    const unitCost = clampNonNeg(row.unitCost);
    const ctr = clampNonNeg(row.ctr); // e.g., 0.02 => 2%
    const convRate = clampNonNeg(row.conversionRate); // e.g., 0.03 => 3%
    const aov = clampNonNeg(row.aov);

    let impressions = 0;
    let clicks = 0;
    if (row.model === 'CPC') {
      clicks = unitCost > 0 ? budget / unitCost : 0;
      impressions = 0; // unknown without CTR; leave as 0/- for display
    } else if (row.model === 'CPM') {
      impressions = unitCost > 0 ? (budget / unitCost) * 1000 : 0;
      clicks = impressions * ctr;
    }
    const conversions = clicks * convRate;
    const revenue = conversions * aov;
    const cpa = conversions > 0 ? budget / conversions : 0;
    const roas = budget > 0 ? revenue / budget : 0;

    return { impressions, clicks, conversions, revenue, cpa, roas };
  }

  // Branding assumptions helpers
  // Supported models: CPM (impressions), CPV (views)
  function computeBranding(row) {
    const budget = clampNonNeg(row.budget);
    const unitCost = clampNonNeg(row.unitCost);
    const frequency = clampNonNeg(row.frequency || 0);

    let impressions = 0;
    let views = 0;
    if (row.model === 'CPM') {
      impressions = unitCost > 0 ? (budget / unitCost) * 1000 : 0;
    } else if (row.model === 'CPV') {
      views = unitCost > 0 ? budget / unitCost : 0;
    }
    const totalContacts = impressions + views; // combined exposure
    const reach = frequency > 0 ? totalContacts / frequency : 0;
    return { impressions, views, reach, frequency };
  }

  // Plans per month
  const months = ['Ocak', 'Şubat', 'Mart'];

  const performancePlans = {
    'Ocak': [
      // Adjusted to monthly share of 2M total (25% month; 80% performance)
      { channel: 'Google Ads', model: 'CPC', budget: 236600, unitCost: 2.28, conversionRate: 0.0468, aov: 2218 },
      { channel: 'Meta', model: 'CPC', budget: 145900, unitCost: 5.67, conversionRate: 0.00497, aov: 1672 },
      { channel: 'Yandex Ads', model: 'CPM', budget: 17500, unitCost: 35, ctr: 0.012, conversionRate: 0.02, aov: 950 },
    ],
    'Şubat': [
      // 45% month; 80% performance
      { channel: 'Google Ads', model: 'CPC', budget: 423360, unitCost: 2.28, conversionRate: 0.0468, aov: 2218 },
      { channel: 'Meta', model: 'CPC', budget: 261144, unitCost: 5.67, conversionRate: 0.00497, aov: 1672 },
      { channel: 'Yandex Ads', model: 'CPM', budget: 35496, unitCost: 35, ctr: 0.012, conversionRate: 0.02, aov: 950 },
    ],
    'Mart': [
      // 30% month; 80% performance
      { channel: 'Google Ads', model: 'CPC', budget: 280704, unitCost: 2.28, conversionRate: 0.0468, aov: 2218 },
      { channel: 'Meta', model: 'CPC', budget: 173160, unitCost: 5.67, conversionRate: 0.00497, aov: 1672 },
      { channel: 'Yandex Ads', model: 'CPM', budget: 26136, unitCost: 35, ctr: 0.012, conversionRate: 0.02, aov: 960 },
    ],
  };

  const brandingPlans = {
    'Ocak': [
      // 25% month; 20% branding of month (100k total)
      { channel: 'Programmatic', model: 'CPM', budget: 29600, unitCost: 42, frequency: 3 },
      { channel: 'YouTube', model: 'CPV', budget: 33400, unitCost: 0.20, frequency: 2 },
      { channel: 'Display Network', model: 'CPM', budget: 14800, unitCost: 30, frequency: 2.5 },
      { channel: 'Dizi (TV Spots)', model: 'CPV', budget: 22200, unitCost: 0.60, frequency: 2 },
    ],
    'Şubat': [
      // 45% month; 20% branding of month (180k total)
      { channel: 'Programmatic', model: 'CPM', budget: 54000, unitCost: 42, frequency: 3 },
      { channel: 'YouTube', model: 'CPV', budget: 57000, unitCost: 0.20, frequency: 2 },
      { channel: 'Display Network', model: 'CPM', budget: 27000, unitCost: 30, frequency: 2.5 },
      { channel: 'Dizi (TV Spots)', model: 'CPV', budget: 42000, unitCost: 0.58, frequency: 2 },
    ],
    'Mart': [
      // 30% month; 20% branding of month (120k total)
      { channel: 'Programmatic', model: 'CPM', budget: 35300, unitCost: 42, frequency: 3 },
      { channel: 'YouTube', model: 'CPV', budget: 38800, unitCost: 0.20, frequency: 2 },
      { channel: 'Display Network', model: 'CPM', budget: 17600, unitCost: 30, frequency: 2.5 },
      { channel: 'Dizi (TV Spots)', model: 'CPV', budget: 28300, unitCost: 0.58, frequency: 2 },
    ],
  };

  function renderPerformanceTable(rows, title) {
    let totals = { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">{title || 'Performance'}</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Channel</th>
              <th className="text-center py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Model</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Unit Cost</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Budget</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Impr.</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Clicks</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Conv.</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Revenue</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">CPA</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">ROAS</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {rows.map((r, idx) => {
              const d = computePerformance(r);
              totals.budget += r.budget;
              totals.impressions += d.impressions;
              totals.clicks += d.clicks;
              totals.conversions += d.conversions;
              totals.revenue += d.revenue;
              return (
                <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  <td className="py-2 px-3 text-sm text-gray-900">{r.channel}</td>
                  <td className="py-2 px-3 text-center text-sm">{r.model}</td>
                  <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : formatTL(r.unitCost, 2)}</td>
                  <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(r.budget)}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.impressions ? formatInt(d.impressions) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.clicks ? formatInt(d.clicks) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.conversions ? formatInt(d.conversions) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{formatTL(d.revenue, 0)}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.cpa ? formatTL(d.cpa, 0) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.roas ? `${d.roas.toFixed(2)}x` : '-'}</td>
                </tr>
              );
            })}
            <tr className="bg-gray-100">
              <td className="py-2 px-3 text-sm font-medium" colSpan={3}>TOTAL</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(totals.budget)}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.impressions ? formatInt(totals.impressions) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.clicks ? formatInt(totals.clicks) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.conversions ? formatInt(totals.conversions) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(totals.revenue, 0)}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
              <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  function renderBrandingTable(rows, title) {
    let totals = { budget: 0, impressions: 0, views: 0, reach: 0 };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">{title || 'Branding'}</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Channel</th>
              <th className="text-center py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Model</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Unit Cost</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Budget</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Impr.</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Views</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Reach</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">CPM</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {rows.map((r, idx) => {
              const d = computeBranding(r);
              totals.budget += r.budget;
              totals.impressions += d.impressions;
              totals.views += d.views;
              totals.reach += d.reach;
              return (
                <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                  <td className="py-2 px-3 text-sm text-gray-900">{r.channel}</td>
                  <td className="py-2 px-3 text-center text-sm">{r.model}</td>
                  <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : formatTL(r.unitCost, 2)}</td>
                  <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(r.budget)}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.impressions ? formatInt(d.impressions) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.views ? formatInt(d.views) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{d.reach ? formatInt(d.reach) : '-'}</td>
                  <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : '-'}</td>
                </tr>
              );
            })}
            <tr className="bg-gray-100">
              <td className="py-2 px-3 text-sm font-medium" colSpan={3}>TOTAL</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(totals.budget)}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.impressions ? formatInt(totals.impressions) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.views ? formatInt(totals.views) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{totals.reach ? formatInt(totals.reach) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const quarterTotals = months.reduce((acc, m) => {
    const perfRows = performancePlans[m] || [];
    const brandRows = brandingPlans[m] || [];
    const perfAgg = perfRows.reduce((p, r) => {
      const d = computePerformance(r);
      p.budget += r.budget;
      p.revenue += d.revenue;
      return p;
    }, { budget: 0, revenue: 0 });
    const brandAgg = brandRows.reduce((p, r) => {
      const d = computeBranding(r);
      p.budget += r.budget;
      p.reach += d.reach;
      p.impressions += d.impressions;
      p.views += d.views;
      return p;
    }, { budget: 0, reach: 0, impressions: 0, views: 0 });
    acc.perfBudget += perfAgg.budget;
    acc.perfRevenue += perfAgg.revenue;
    acc.brandBudget += brandAgg.budget;
    acc.brandReach += brandAgg.reach;
    acc.brandImpressions += brandAgg.impressions;
    acc.brandViews += brandAgg.views;
    return acc;
  }, { perfBudget: 0, perfRevenue: 0, brandBudget: 0, brandReach: 0, brandImpressions: 0, brandViews: 0 });

  function renderCombinedMonthTable(month) {
    const perfRows = performancePlans[month] || [];
    const brandRows = brandingPlans[month] || [];

    const perfTotals = perfRows.reduce((p, r) => {
      const d = computePerformance(r);
      p.budget += r.budget;
      p.impressions += d.impressions;
      p.clicks += d.clicks;
      p.conversions += d.conversions;
      p.revenue += d.revenue;
      return p;
    }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });

    const brandTotals = brandRows.reduce((p, r) => {
      const d = computeBranding(r);
      p.budget += r.budget;
      p.impressions += d.impressions;
      p.views += d.views;
      p.reach += d.reach;
      return p;
    }, { budget: 0, impressions: 0, views: 0, reach: 0 });

    const monthTotals = {
      budget: perfTotals.budget + brandTotals.budget,
      impressions: perfTotals.impressions + brandTotals.impressions,
      views: brandTotals.views,
      reach: brandTotals.reach,
      clicks: perfTotals.clicks,
      conversions: perfTotals.conversions,
      revenue: perfTotals.revenue,
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">{month} 2026 — Plan</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Channel</th>
                <th className="text-center py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Model</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Unit Cost</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Budget</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Impr.</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Clicks</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Conv.</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Revenue</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">CPA</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">ROAS</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Views</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Reach</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">CPM</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {/* Performance section header */}
              <tr className="bg-gray-50">
                <td
                  className="py-3 px-3 text-[13px] font-bold text-gray-900 uppercase tracking-wide border-t-2 border-b-2 border-gray-300"
                  colSpan={13}
                >
                  Performance
                </td>
              </tr>
              {perfRows.map((r, idx) => {
                const d = computePerformance(r);
                return (
                  <tr key={`perf-${idx}`} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                    <td className="py-2 px-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        {channelIcons[r.channel] && (
                          <img
                            src={channelIcons[r.channel]}
                            alt={`${r.channel} logo`}
                            className={`${channelIconSize[r.channel] || 'w-4 h-4'} object-contain`}
                          />
                        )}
                        <span>{r.channel}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-center text-sm">{r.model}</td>
                    <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : formatTL(r.unitCost, 2)}</td>
                    <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(r.budget)}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.impressions ? formatInt(d.impressions) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.clicks ? formatInt(d.clicks) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.conversions ? formatInt(d.conversions) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{formatTL(d.revenue, 0)}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.cpa ? formatTL(d.cpa, 0) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.roas ? `${d.roas.toFixed(2)}x` : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                  </tr>
                );
              })}
              {/* Performance subtotal */}
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                <td className="py-2 px-3 text-sm font-medium" colSpan={3}>TOTAL (Performance)</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(perfTotals.budget)}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{perfTotals.impressions ? formatInt(perfTotals.impressions) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{perfTotals.clicks ? formatInt(perfTotals.clicks) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{perfTotals.conversions ? formatInt(perfTotals.conversions) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(perfTotals.revenue, 0)}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
              </tr>

              {/* Branding section header */}
              <tr className="bg-gray-50">
                <td
                  className="py-3 px-3 text-[13px] font-bold text-gray-900 uppercase tracking-wide border-t-2 border-b-2 border-gray-300"
                  colSpan={13}
                >
                  Branding
                </td>
              </tr>
              {brandRows.map((r, idx) => {
                const d = computeBranding(r);
                return (
                  <tr key={`brand-${idx}`} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                    <td className="py-2 px-3 text-sm text-gray-900">{r.channel}</td>
                    <td className="py-2 px-3 text-center text-sm">{r.model}</td>
                    <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : formatTL(r.unitCost, 2)}</td>
                    <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(r.budget)}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.impressions ? formatInt(d.impressions) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">-</td>
                    <td className="py-2 px-3 text-right text-sm">{d.views ? formatInt(d.views) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{d.reach ? formatInt(d.reach) : '-'}</td>
                    <td className="py-2 px-3 text-right text-sm">{r.model === 'CPM' ? formatTL(r.unitCost) : '-'}</td>
                  </tr>
                );
              })}
              {/* Branding subtotal */}
              <tr className="bg-gray-100 border-t-2 border-gray-300">
                <td className="py-2 px-3 text-sm font-medium" colSpan={3}>TOTAL (Branding)</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(brandTotals.budget)}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{brandTotals.impressions ? formatInt(brandTotals.impressions) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{brandTotals.views ? formatInt(brandTotals.views) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{brandTotals.reach ? formatInt(brandTotals.reach) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
              </tr>

              {/* Month grand total */}
              <tr className="bg-gray-200 border-t-2 border-gray-300">
                <td className="py-2 px-3 text-sm font-medium" colSpan={3}>MONTH TOTAL</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(monthTotals.budget)}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{monthTotals.impressions ? formatInt(monthTotals.impressions) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{monthTotals.clicks ? formatInt(monthTotals.clicks) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{monthTotals.conversions ? formatInt(monthTotals.conversions) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTL(monthTotals.revenue, 0)}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{monthTotals.views ? formatInt(monthTotals.views) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{monthTotals.reach ? formatInt(monthTotals.reach) : '-'}</td>
                <td className="py-2 px-3 text-right text-sm font-semibold">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderSummaryMonthTable(month) {
    const perfRows = performancePlans[month] || [];
    const brandRows = brandingPlans[month] || [];

    const perfTotals = perfRows.reduce((p, r) => {
      const d = computePerformance(r);
      p.budget += r.budget;
      p.impressions += d.impressions;
      p.clicks += d.clicks;
      p.conversions += d.conversions;
      p.revenue += d.revenue;
      return p;
    }, { budget: 0, impressions: 0, clicks: 0, conversions: 0, revenue: 0 });

    const brandTotals = brandRows.reduce((p, r) => {
      const d = computeBranding(r);
      p.budget += r.budget;
      p.impressions += d.impressions;
      p.views += d.views;
      p.reach += d.reach;
      return p;
    }, { budget: 0, impressions: 0, views: 0, reach: 0 });

    const monthTotals = {
      budget: perfTotals.budget + brandTotals.budget,
      impressions: perfTotals.impressions + brandTotals.impressions,
      clicks: perfTotals.clicks,
      conversions: perfTotals.conversions,
      revenue: perfTotals.revenue,
      views: brandTotals.views,
      reach: brandTotals.reach,
    };

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">{month} 2026 — Özet</h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="text-left py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Kategori</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Budget</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Impr.</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Clicks</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Conv.</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Revenue</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Views</th>
              <th className="text-right py-1.5 px-2 text-[10px] font-medium text-gray-600 uppercase tracking-wide whitespace-nowrap">Reach</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            <tr className="border-b border-gray-100">
              <td className="py-1.5 px-2 text-[11px] font-semibold text-gray-900 whitespace-nowrap">Performance</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{formatTLCompact(perfTotals.budget)}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{perfTotals.impressions ? formatNumberCompact(perfTotals.impressions) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{perfTotals.clicks ? formatNumberCompact(perfTotals.clicks) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{perfTotals.conversions ? formatNumberCompact(perfTotals.conversions) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{formatTLCompact(perfTotals.revenue)}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">-</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">-</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 px-2 text-[11px] font-semibold text-gray-900 whitespace-nowrap">Branding</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{formatTLCompact(brandTotals.budget)}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{brandTotals.impressions ? formatNumberCompact(brandTotals.impressions) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">-</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">-</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">-</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{brandTotals.views ? formatNumberCompact(brandTotals.views) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] whitespace-nowrap">{brandTotals.reach ? formatNumberCompact(brandTotals.reach) : '-'}</td>
            </tr>
            <tr className="bg-gray-100 border-t-2 border-gray-300">
              <td className="py-1.5 px-2 text-[11px] font-medium">MONTH TOTAL</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{formatTLCompact(monthTotals.budget)}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{monthTotals.impressions ? formatNumberCompact(monthTotals.impressions) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{monthTotals.clicks ? formatNumberCompact(monthTotals.clicks) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{monthTotals.conversions ? formatNumberCompact(monthTotals.conversions) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{formatTLCompact(monthTotals.revenue)}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{monthTotals.views ? formatNumberCompact(monthTotals.views) : '-'}</td>
              <td className="py-1.5 px-2 text-right text-[11px] font-semibold text-gray-900 whitespace-nowrap">{monthTotals.reach ? formatNumberCompact(monthTotals.reach) : '-'}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  function renderQuarterSummaryTable() {
    return (
      <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">2026 Q1 — Özet</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Kategori</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Budget</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Impr.</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Clicks</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Conv.</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Revenue</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Views</th>
              <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Reach</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            <tr className="border-b border-gray-100">
              <td className="py-2 px-3 text-sm font-semibold text-gray-900">Performance</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTLCompact(quarterTotals.perfBudget)}</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">{formatTLCompact(quarterTotals.perfRevenue)}</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-2 px-3 text-sm font-semibold text-gray-900">Branding</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTLCompact(quarterTotals.brandBudget)}</td>
              <td className="py-2 px-3 text-right text-sm">{quarterTotals.brandImpressions ? formatNumberCompact(quarterTotals.brandImpressions) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">-</td>
              <td className="py-2 px-3 text-right text-sm">{quarterTotals.brandViews ? formatNumberCompact(quarterTotals.brandViews) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm">{quarterTotals.brandReach ? formatNumberCompact(quarterTotals.brandReach) : '-'}</td>
            </tr>
            <tr className="bg-gray-100 border-t-2 border-gray-300">
              <td className="py-2 px-3 text-sm font-medium">Q1 TOTAL</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTLCompact(quarterTotals.perfBudget + quarterTotals.brandBudget)}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{quarterTotals.brandImpressions ? formatNumberCompact(quarterTotals.brandImpressions) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">-</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">-</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{formatTLCompact(quarterTotals.perfRevenue)}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{quarterTotals.brandViews ? formatNumberCompact(quarterTotals.brandViews) : '-'}</td>
              <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">{quarterTotals.brandReach ? formatNumberCompact(quarterTotals.brandReach) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  const targetingAudience = {
    'Programmatic': [
      'Apple ekosistemi: iPhone/Mac/iPad/Watch sahipleri ve look‑alike segmentler',
      'iPhone upgrade niyeti (sözleşme yenileme / yeni model araştıranlar)',
      'MacBook (M1/M2/M3) içerik tüketen yaratıcı profesyoneller (tasarım, video, müzik)',
      'Üniversite/öğrenci laptop & tablet arayışı (Back‑to‑School, eğitim indirimleri)',
      'Fotoğraf‑video prodüksiyon, içerik üreticileri, mobil fotoğrafçılık ilgisi',
      'Premium aksesuar alıcıları (AirPods, Apple Pencil, MagSafe, klavye/cover)',
      'Remarketing: ürün sayfası, sepet, mağaza konum sayfası ziyaretçileri'
    ],
    'YouTube': [
      'Apple lansman & keynote özetleri, “What’s new in iPhone/Mac/iPad” videoları',
      'iPhone/Mac karşılaştırma ve satın alma rehberleri (“Which Mac to buy?”)',
      'Final Cut Pro / Logic Pro / Xcode eğitimleri (yaratıcı & developer kitle)',
      'Üniversiteye hazırlık: “okula dönüş için laptop/iPad önerileri”',
      'Mobil fotoğrafçılık, sinematik video, AirPods/Watch aksesuar incelemeleri'
    ],
    'Display Network': [
      'Apple ekosistemi topluluk & inceleme içerikleri (genel teknoloji yayınları)',
      'Operatör cihaz kampanyaları / taksit sayfaları (cihaz yenileme niyeti)',
      'E‑ticaret teknoloji kategorileri: premium telefon/laptop/tablet sayfaları',
      'Yaratıcı yazılım/hardware içerikleri (video, ses, fotoğraf üretimi)',
      'Yerel remarketing ve kategori/ürün sayfası tekrar hedefleme'
    ],
    'Dizi (TV Spots)': [
      'AB sosyoekonomik gruba erişen prime‑time kuşakları (20:00–22:00)',
      'Spor büyük maç haftaları (yüksek erişim uçları)',
      'Lansman haftalarında premium spot yerleşimi (ilk & son kuşak)',
      'Üniversite kayıt/okula dönüş dönemlerinde yoğunlaştırılmış frekans'
    ]
  };

  const targetingPlacement = {
    'Programmatic': [
      'Premium Tech/IT yayınları, cihaz inceleme ve “satın alma rehberi” içerikleri',
      'Operatör cihaz kampanyaları / taksit ve takas sayfaları',
      'Elektronik perakende & kıyaslama portalları (telefon/tablet/laptop kategorileri)',
      'Contextual: “iPhone 16”, “MacBook Pro M3”, “iPad Pro OLED”, “AirPods Pro 2”, “Apple Watch”, “iPhone takas”, “Apple servis”, “MagSafe”'
    ],
    'YouTube': [
      'Apple odaklı/teknoloji inceleme kanalları (review/unboxing/performance tests)',
      'Üniversite/öğrenci içerikleri (okula dönüş ekipman videoları)',
      'Yaratıcı prodüksiyon kanalları (video/fotoğraf/müzik setup içerikleri)',
      'Aksesuar review kanalları (AirPods/Watch/klavye vs.)'
    ],
    'Display Network': [
      'Teknoloji inceleme blogları ve toplulukları',
      'Operatör & üretici cihaz kampanya sayfaları',
      'Elektronik perakende vitrin/kategori sayfaları (premium segment)',
      'Negatif domain & brand‑safety listeleri ile temiz envanter'
    ],
    'Dizi (TV Spots)': [
      'Prime‑time ilk/son spot, yüksek erişimli dizi kuşakları',
      'Büyük spor karşılaşmaları öncesi sonrası kısa kuşaklar',
      'Apple lansman ve okula dönüş dönemlerinde yoğun planlama',
      'Bölge bazlı GRP optimizasyonu ve frekans kontrolü'
    ]
  };

  function renderTargetingTable(mapObj, title) {
    const entries = Object.keys(mapObj);
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide" style={{width: '22%'}}>Channel</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Targeting Suggestions</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {entries.map((ch, idx) => (
              <tr key={ch} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                <td className="py-2 px-3 text-sm font-medium text-gray-900">{ch}</td>
                <td className="py-2 px-3 text-sm text-gray-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {mapObj[ch].map((v, i) => (<li key={i}>{v}</li>))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // MacFit Apple Watch kampanyası için özet plan tablosu (17–31 Aralık, toplam 20K)
  function renderMacfitAppleWatchPlanTable() {
    const rows = [
      {
        channel: 'Meta — Remarketing',
        objective: 'Reach (Remarketing)',
        model: 'CPM',
        unitCost: 60,
        budget: 12000,
        impressions: 200000,
        reach: 140000
      },
      {
        channel: 'Meta — Awareness + Reach',
        objective: 'Awareness / Reach',
        model: 'CPM',
        unitCost: 40,
        budget: 8000,
        impressions: 200000,
        reach: 150000
      }
    ];

    const totalBudget = rows.reduce((sum, r) => sum + (r.budget || 0), 0);
    const totalImpr = rows.reduce((sum, r) => sum + (r.impressions || 0), 0);
    const totalReach = rows.reduce((sum, r) => sum + (r.reach || 0), 0);
    const totalFreq = totalReach > 0 ? totalImpr / totalReach : 0;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">MacFit Apple Watch — 17–31 Aralık Plan (Toplam {formatTL(totalBudget)})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Channel</th>
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Objective</th>
                <th className="text-center py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Model</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Unit Cost</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Budget</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Impr.</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Reach</th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Freq.</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.map((r, idx) => (
                <tr
                  key={r.channel}
                  className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <td className="py-2 px-3 text-sm text-gray-900 whitespace-nowrap">{r.channel}</td>
                  <td className="py-2 px-3 text-sm text-gray-800 whitespace-nowrap">{r.objective}</td>
                  <td className="py-2 px-3 text-center text-sm whitespace-nowrap">{r.model}</td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.unitCost ? formatTL(r.unitCost, 0) : '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                    {formatTL(r.budget)}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.impressions ? formatInt(r.impressions) : '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.reach ? formatInt(r.reach) : '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.impressions && r.reach ? (r.impressions / r.reach).toFixed(1) : '-'}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td className="py-2 px-3 text-sm font-medium" colSpan={4}>
                  MONTH TOTAL
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {formatTL(totalBudget)}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalImpr ? formatInt(totalImpr) : '-'}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalReach ? formatInt(totalReach) : '-'}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalFreq ? totalFreq.toFixed(1) : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Yılbaşı Apple Watch kampanyası için plan tablosu (15 gün, toplam 35K)
  function renderNewYearAppleWatchPlanTable() {
    const rows = [
      {
        channel: 'Google Ads — Search',
        objective: 'Sales / High Intent',
        model: 'CPC',
        unitCost: 6.5,
        budget: 15000,
        impressions: 120000,
        // Search kampanyası için reach / frekans raporlamıyoruz
        reach: 0
      },
      {
        channel: 'Meta — Awareness',
        objective: 'Awareness / Reach',
        model: 'CPM',
        unitCost: 40,
        budget: 10000,
        impressions: 220000,
        reach: 150000
      },
      {
        channel: 'Meta — Sales',
        objective: 'Sales / Conversions',
        model: 'CPM',
        unitCost: 55,
        budget: 10000,
        impressions: 160000,
        reach: 90000
      }
    ];

    const totalBudget = rows.reduce((sum, r) => sum + (r.budget || 0), 0);
    const totalImpr = rows.reduce((sum, r) => sum + (r.impressions || 0), 0);
    const totalReach = rows.reduce((sum, r) => sum + (r.reach || 0), 0);
    const totalFreq = totalReach > 0 ? totalImpr / totalReach : 0;

    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
          <h3 className="text-sm font-semibold">
            Yılbaşı Apple Watch — 15 Günlük Kampanya Planı (Toplam {formatTL(totalBudget)})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Channel
                </th>
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Objective
                </th>
                <th className="text-center py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Model
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Unit Cost
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Budget
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Impr.
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Reach
                </th>
                <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Freq.
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.map((r, idx) => (
                <tr
                  key={r.channel}
                  className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <td className="py-2 px-3 text-sm text-gray-900 whitespace-nowrap">{r.channel}</td>
                  <td className="py-2 px-3 text-sm text-gray-800 whitespace-nowrap">{r.objective}</td>
                  <td className="py-2 px-3 text-center text-sm whitespace-nowrap">{r.model}</td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.model === 'CPC' ? formatTL(r.unitCost, 2) : formatTL(r.unitCost, 0)}
                  </td>
                  <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                    {formatTL(r.budget)}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.impressions ? formatInt(r.impressions) : '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.reach ? formatInt(r.reach) : '-'}
                  </td>
                  <td className="py-2 px-3 text-right text-sm text-gray-800">
                    {r.impressions && r.reach ? (r.impressions / r.reach).toFixed(1) : '-'}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-100">
                <td className="py-2 px-3 text-sm font-medium" colSpan={4}>
                  CAMPAIGN TOTAL
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {formatTL(totalBudget)}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalImpr ? formatInt(totalImpr) : '-'}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalReach ? formatInt(totalReach) : '-'}
                </td>
                <td className="py-2 px-3 text-right text-sm font-semibold text-gray-900">
                  {totalFreq ? totalFreq.toFixed(1) : '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#F7F8FC] min-h-screen space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Gürgençler — 2026 Q1 Plan</h1>
          <p className="text-sm text-gray-500 mt-1">Ocak, Şubat, Mart — Performance ve Branding planları</p>
        </div>
      </div>

      {/* Quarter summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Q1 Performance Budget</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">{formatTL(quarterTotals.perfBudget)}</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Q1 Performance Revenue (est.)</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">{formatTL(quarterTotals.perfRevenue)}</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Q1 Branding Budget</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">{formatTL(quarterTotals.brandBudget)}</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-xs text-gray-500">Q1 Branding Reach (est.)</div>
          <div className="text-lg font-semibold text-gray-800 mt-1">{formatInt(quarterTotals.brandReach)}</div>
        </div>
      </div>

      {months.map((m) => (
        <div key={m} className="space-y-3">
          {renderCombinedMonthTable(m)}
        </div>
      ))}

      {/* Monthly summary tables without platform details */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">Özet Tablolar (Platform detayları olmadan)</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {months.map((m) => (
            <div key={`summary-${m}`}>
              {renderSummaryMonthTable(m)}
            </div>
          ))}
        </div>
        {renderQuarterSummaryTable()}
      </div>

      {/* Targeting tables */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">Targeting Çalışması</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {renderTargetingTable(targetingAudience, 'Audience & Intent Targeting')}
          {renderTargetingTable(targetingPlacement, 'Placement & Context Targeting')}
        </div>
      </div>

      {/* Branding Assets examples */}
      {(() => {
        const brandingAssets = {
          'Programmatic': [
            'Static HTML5 Banner Set: 300x250, 336x280, 300x600, 160x600, 728x90, 970x250 — iPhone 16 / MacBook Pro M3 görselleri, CTA: “Hemen İncele / Mağazada Dene”',
            'Motion Banner: iPhone 360°/parallax kısa animasyon, final end‑card’da Gürgençler logo + en yakın mağaza',
            'Co‑brand KV: Banka taksit/taşıt kampanyası (koşullar altında), trade‑in vurgusu'
          ],
          'YouTube': [
            '6s Bumper: “iPhone’unu yenile — Trade‑in ile avantajlı geçiş” + logo end‑card',
            '15s Skippable: iPhone/Mac USP cut + mağazada deneyim CTA',
            'App stock montaj: iMovie/Final Cut/Logic/Notability gibi uygulama stock videoları, finalde Gürgençler logo ve mağaza bilgisi',
            'YouTube Shorts: 9:16 dikey quick tips (iPhone kamera, Mac kısayollar) UGC formatında'
          ],
          'Display Network': [
            'Rich Media Expandable: ürün hero + renk varyasyonları, “Hemen İncele”',
            'Dynamik feed’li aksesuar setleri (AirPods, Apple Pencil, MagSafe) upsell'
          ],
          'Dizi (TV Spots)': [
            '10s/15s TV cut: cihaz hero + fiyat/taksit süperleri, kısa jingle, güçlü end‑card',
            'Lansman haftası: ilk/son spot varyantı, lokasyon tag’li end‑card'
          ],
          'Banking (Co‑brand)': [
            'Banka logolu KV & video tag: “X Banka ile 12 taksit / %0 faiz*” (*yasal metin)',
            'Trade‑in + taksit kombinasyonunu anlatan 6s ve 15s varyant'
          ]
        };
        const renderAssets = () => (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
              <h3 className="text-sm font-semibold">Branding Asset Örnekleri</h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide" style={{width: '22%'}}>Kanal</th>
                  <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">Örnek Assetler</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {Object.keys(brandingAssets).map((k, idx) => (
                  <tr key={k} className={`border-b border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                    <td className="py-2 px-3 text-sm font-medium text-gray-900">{k}</td>
                    <td className="py-2 px-3 text-sm text-gray-700">
                      <ul className="list-disc pl-5 space-y-1">
                        {brandingAssets[k].map((v, i) => (<li key={i}>{v}</li>))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        const assetsSection = (
          <div className="space-y-4">
            {renderAssets()}
          </div>
        );
        return assetsSection;
      })()}

      {/* Ek Kampanyalar: Apple Watch */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">Ek Kampanyalar — Apple Watch</h2>

        {/* 1) MacFit Apple Watch Remarketing (17–31 Aralık) */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
            <h3 className="text-sm font-semibold">MacFit Apple Watch — 17–31 Aralık (Meta Remarketing)</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Alan
                </th>
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Plan
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Dönem</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  17–31 Aralık (15 gün), MacFit kulüplerinde Apple Watch odaklı kampanya.
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Platform & Objective</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Meta Ads — <span className="font-semibold">Awareness</span> ve{' '}
                  <span className="font-semibold">Reach</span> objective kombinasyonu
                  (kampanya seviyesinde erişim ve frekans kontrolü ile).
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Remarketing</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Web & app remarketing (Apple Watch ürün / sepet / kategori sayfaları, MacFit lokasyon sayfaları,
                  mağaza yönlendirme CTA'ları). Son 30–60 gün ziyaretçileri, özel “Apple Watch ilgisi” segmentleri.
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Interest + Lokasyon</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Lokasyon bazlı interest targeting:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>MacFit kulüplerine yakın (1–3 km) konum balonları</li>
                    <li>Fitness, gym, koşu, bisiklet, pilates, sağlıklı yaşam ilgileri</li>
                    <li>Apple Watch / akıllı saat, fitness tracker, spor teknolojileri ilgileri</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 1.a) MacFit Apple Watch Bütçe Planı (20.000₺) */}
        {renderMacfitAppleWatchPlanTable()}

        {/* 2) Yılbaşı Apple Watch Kampanyası (15 gün) */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 flex justify-between items-center bg-black text-white">
            <h3 className="text-sm font-semibold">Yılbaşı Apple Watch — 15 Günlük Kampanya</h3>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Kanal
                </th>
                <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wide">
                  Rol & Objective
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Google Ads — Search</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Yüksek niyetli “Apple Watch” aramaları:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Apple Watch, Apple Watch fiyat, Apple Watch satın al, Apple Watch kampanya</li>
                    <li>Apple Watch hediye, Apple Watch yılbaşı hediyesi, Apple Watch indirim</li>
                    <li>Marka + generik kombinasyonları; marka adı + “Apple Watch” ve lokasyon ekleri</li>
                  </ul>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Meta — Awareness</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Yılbaşı öncesi geniş kitlede Apple Watch bilinirliği ve hediye konumlandırması:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>Video + carousel formatları, hediye kutusu / yılbaşı görsel dili</li>
                    <li>Apple ekosistemi ilgileri, premium gelir segmentleri, teknoloji & spor ilgileri</li>
                    <li>“Son 15 gün” mesajlı geri sayım kreatifleri (FOMO, limited time)</li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td className="py-2 px-3 text-sm font-medium text-gray-900">Meta — Sales / Conversions</td>
                <td className="py-2 px-3 text-sm text-gray-800">
                  Web / app satış hedefli kampanya:
                  <ul className="list-disc pl-5 mt-1 space-y-1">
                    <li>ViewContent, AddToCart, Purchase optimizasyonlu remarketing setleri</li>
                    <li>Sepet abandoner’lar, Apple Watch ürün sayfası ziyaretçileri, Black Friday + Aralık dönemi yeni kullanıcıları</li>
                    <li>Dinamik ürün reklamları (DPA) ile Apple Watch modelleri ve kayış/aksesuar upsell’leri</li>
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* 2b) Yılbaşı Apple Watch Plan Tablosu */}
        {renderNewYearAppleWatchPlanTable()}
      </div>
    </div>
  );
}


